/**
 * ScheduledExport — admin panel for scheduled Board packet delivery.
 * Writes to PLANNED `/v3/audit-reports/schedule`. Today the UI is form-only;
 * save dispatches a staff-note audit event and shows a confirmation.
 */

import { useState } from 'react';
import { Button, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';

export function ScheduledExport(): JSX.Element {
  const api = useApiClient();
  const { user } = useAuth();
  const [cadence, setCadence] = useState<'weekly' | 'monthly' | 'quarterly'>('monthly');
  const [dayOfWeek, setDayOfWeek] = useState<string>('Monday');
  const [hour, setHour] = useState<string>('08:00');
  const [recipients, setRecipients] = useState<string>('ceo@mypcb.com, cfo@mypcb.com');
  const [saving, setSaving] = useState<boolean>(false);
  const [status, setStatus] = useState<string | null>(null);

  async function save(): Promise<void> {
    if (!user?.upn) return;
    setSaving(true);
    setStatus(null);
    try {
      // TODO Wave 3: api.retail.auditReports.setSchedule(...)
      await api.retail.staff.sendNote({
        fromEmail: user.upn,
        fromName: user.displayName,
        toEmail: user.upn,
        toName: user.displayName,
        messageBody: `[SCHEDULED-PACKET] cadence=${cadence} day=${dayOfWeek} hour=${hour} to=${recipients}`,
        contextLabel: 'RRM-ScheduledExport',
      });
      setStatus('Schedule recorded. Live delivery ships with /v3/audit-reports/schedule.');
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="module-page">
      <header className="module-page__header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-6)' }}>
          <h1>RRM · Scheduled board-packet delivery</h1>
          <Pill variant="muted">Admin</Pill>
        </div>
        <p className="module-landing__scope">
          Auto-delivers the Board packet to stakeholders on a cadence.
        </p>
      </header>

      <div style={{ display: 'grid', gap: 'var(--sp-7)', maxWidth: 560, background: 'var(--surface-card)', padding: 'var(--sp-9)', border: '1px solid var(--surface-border)', borderRadius: 'var(--r-lg)' }}>
        <label style={{ display: 'grid', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--pcb-blue-dark)' }}>
          Cadence
          <select value={cadence} onChange={(e) => setCadence(e.target.value as typeof cadence)} style={{ padding: 'var(--sp-5)', border: '1px solid var(--surface-border)', borderRadius: 'var(--r-md)', fontFamily: 'inherit' }}>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
          </select>
        </label>
        <label style={{ display: 'grid', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--pcb-blue-dark)' }}>
          Day of week
          <select value={dayOfWeek} onChange={(e) => setDayOfWeek(e.target.value)} style={{ padding: 'var(--sp-5)', border: '1px solid var(--surface-border)', borderRadius: 'var(--r-md)', fontFamily: 'inherit' }}>
            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </label>
        <label style={{ display: 'grid', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--pcb-blue-dark)' }}>
          Delivery time (CT)
          <input type="time" value={hour} onChange={(e) => setHour(e.target.value)} style={{ padding: 'var(--sp-5)', border: '1px solid var(--surface-border)', borderRadius: 'var(--r-md)', fontFamily: 'inherit' }} />
        </label>
        <label style={{ display: 'grid', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--pcb-blue-dark)' }}>
          Recipients (comma separated)
          <textarea rows={3} value={recipients} onChange={(e) => setRecipients(e.target.value)} style={{ padding: 'var(--sp-5)', border: '1px solid var(--surface-border)', borderRadius: 'var(--r-md)', fontFamily: 'inherit' }} />
        </label>

        {status && (
          <div
            role="status"
            style={{ padding: 'var(--sp-5)', background: 'var(--ok-bg)', color: 'var(--ok-text)', borderRadius: 'var(--r-md)', fontSize: 12 }}
          >
            {status}
          </div>
        )}

        <div style={{ display: 'flex', gap: 'var(--sp-5)', justifyContent: 'flex-end' }}>
          <Button variant="primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save schedule'}
          </Button>
        </div>
      </div>
    </div>
  );
}
